/**
 * ClassAssignmentModal — owner assigns classes to an admin.
 *
 * Two things about this screen are deliberate:
 *
 * 1. An admin with NO classes selected sees NOTHING, rather than everything.
 *    "Empty means unrestricted" is the most common way a permission feature
 *    ships broken, so the empty state says so out loud instead of leaving it
 *    to be inferred.
 *
 * 2. The list only offers classes in the admin's own organization. The server
 *    rejects anything else regardless — this just avoids offering a choice
 *    that will fail.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search, Shield, X } from "lucide-react";
import classAssignmentsService from "../../api/services/classAssignments.service";
import classesService from "../../api/services/classes.service";
import toast from "react-hot-toast";

export default function ClassAssignmentModal({ isOpen, onClose, user }) {
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let cancelled = false;
    setLoading(true);
    setSearch("");
    Promise.all([
      classesService.getAll(),
      classAssignmentsService.listForUser(user.id),
    ])
      .then(([classRes, assigned]) => {
        if (cancelled) return;
        setClasses(classRes?.items || classRes || []);
        const ids = new Set(
          (assigned || []).map((a) => a.class_id || a.id).filter(Boolean),
        );
        setSelected(ids);
        setInitial(ids);
      })
      .catch((e) => {
        console.error("Failed to load class assignments:", e);
        if (!cancelled) toast.error("Could not load class assignments");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return classes;
    return classes.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [classes, search]);

  const dirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const id of selected) if (!initial.has(id)) return true;
    return false;
  }, [selected, initial]);

  if (!isOpen) return null;

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await classAssignmentsService.replaceForUser(user.id, [...selected]);
      toast.success(
        selected.size === 0
          ? `${user.first_name} now has access to no classes.`
          : `${user.first_name} now has access to ${selected.size} class${selected.size === 1 ? "" : "es"}.`,
      );
      onClose(true);
    } catch (e) {
      console.error("Failed to save class assignments:", e);
      toast.error(e?.message || "Could not save class assignments");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold font-manrope text-[#173151] truncate">
                Class Access
              </h3>
              <p className="text-xs font-manrope text-[#666D80] truncate">
                {user?.first_name} {user?.last_name} &middot; {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={() => onClose(false)}
            disabled={saving}
            className="p-1 hover:bg-gray-100 rounded transition shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="w-4 h-4 text-[#666D80] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search classes…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:ring-2 focus:ring-[#F3BC48] focus:border-[#F3BC48]"
            />
          </div>
          <p className="text-xs font-manrope text-[#666D80] mt-2">
            {selected.size === 0 ? (
              <span className="text-orange-600 font-semibold">
                No classes selected — this admin will see no class data at all.
              </span>
            ) : (
              `${selected.size} of ${classes.length} classes selected.`
            )}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-[180px]">
          {loading ? (
            <div className="flex items-center gap-2 text-sm font-manrope text-[#666D80] py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading classes…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm font-manrope text-[#666D80] py-8 text-center">
              No classes match “{search}”.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {filtered.map((c) => {
                const on = selected.has(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => toggle(c.id)}
                      aria-pressed={on}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition ${
                        on
                          ? "border-[#F3BC48] bg-yellow-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <span
                        className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border ${
                          on
                            ? "bg-[#F3BC48] border-[#F3BC48]"
                            : "border-gray-300"
                        }`}
                      >
                        {on && <Check className="w-3.5 h-3.5 text-white" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-manrope text-[#173151] truncate">
                          {c.name}
                        </span>
                        <span className="block text-xs font-manrope text-[#666D80]">
                          {c.current_enrollment || 0}/{c.capacity} enrolled
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 pt-3 border-t border-gray-200">
          <button
            onClick={() => onClose(false)}
            disabled={saving}
            className="px-4 py-2 text-sm font-manrope font-semibold text-[#666D80] hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading || !dirty}
            className="px-4 py-2 text-sm font-manrope font-semibold text-white bg-[#173151] rounded-lg transition hover:bg-[#0f2338] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Saving…" : "Save Access"}
          </button>
        </div>
      </div>
    </div>
  );
}
